import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeftRight, CircleAlert, Coins, PackageMinus, PackageX, Plus, SlidersHorizontal, Boxes } from 'lucide-react'
import { getInventoryItems, getStockMovements } from '../../api/inventory'
import { listRuns } from '../../api/production'
import HelpTip from '../../components/HelpTip'
import Button from '../../components/Button'
import { Card, Tile } from '../../components/Summary'
import { fmtDay } from '../../utils/format'
import { useSession } from '../../session'
import { money } from '../sales/salesShared'
import InventoryTabs from './InventoryTabs'
import { MOVE_TYPES, qty, stockItems, stockState, stockValue, useLocations } from './inventoryShared'

// The Inventory summary, like Sales and Purchase: what the stock is worth,
// what is running out, and what has moved lately.
export default function InventoryMenu() {
  const { approvals, roles, has } = useSession()
  const production = has('production')
  const canBuy = roles.includes('admin') || roles.includes('purchase')
  const locations = useLocations()
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getInventoryItems().catch(() => []),
      getStockMovements({ limit: 6 }).catch(() => []),
      production ? listRuns('Open').catch(() => []) : Promise.resolve(null),
    ]).then(([items, moves, open]) => {
      if (!cancelled) setData({ items: stockItems(items), moves: Array.isArray(moves) ? moves : [], open })
    })
    return () => { cancelled = true }
  }, [production])

  return (
    <div className="section-page">
      <InventoryTabs />
      <header className="section-header">
        <h1>Inventory</h1>
        <div className="section-actions">
          {approvals.valuation > 0 && (
            <Link to="/inventory/valuation-approvals" className="section-chip">
              <CircleAlert size={14} strokeWidth={2} aria-hidden="true" />
              {approvals.valuation} {approvals.valuation === 1 ? 'adjustment' : 'adjustments'} to approve
            </Link>
          )}
          {approvals.counts > 0 && (
            <Link to="/inventory/counts?show=waiting" className="section-chip">
              <CircleAlert size={14} strokeWidth={2} aria-hidden="true" />
              {approvals.counts} {approvals.counts === 1 ? 'count' : 'counts'} to approve
            </Link>
          )}
          {locations?.length > 1 && <Button as={Link} to="/inventory/move" variant="outline" size="sm"><ArrowLeftRight size={14} />Move stock</Button>}
          <Button as={Link} to="/inventory/adjust" variant="outline" size="sm"><SlidersHorizontal size={14} />Adjust stock</Button>
          {canBuy && <Button as={Link} to="/purchase/receipts/new" variant="primary" size="sm"><Plus size={14} />Receive goods</Button>}
        </div>
      </header>

      {data ? <Summary {...data} canBuy={canBuy} /> : <p className="sum-state">Loading stock…</p>}
    </div>
  )
}

function Summary({ items, moves, open, canBuy }) {
  const inStock = items.filter((p) => stockState(p).id !== 'out')
  const low = items.filter((p) => stockState(p).id === 'low').sort((a, b) => Number(a.qty_on_hand) - Number(b.qty_on_hand))
  const out = items.filter((p) => stockState(p).id === 'out')
  const value = items.reduce((s, p) => s + stockValue(p), 0)
  const top = [...inStock].sort((a, b) => stockValue(b) - stockValue(a)).slice(0, 6)

  return (
    <>
      <div className="sum-tiles">
        <Tile label="Stock value" href="/inventory/stock" icon={Coins} value={money(value)} note="At average cost" />
        <Tile label="Products in stock" href="/inventory/stock" icon={Boxes} value={String(inStock.length)}
          note={`of ${items.length} ${items.length === 1 ? 'product' : 'products'}`} />
        <Tile label="Running low" href="/inventory/stock?show=low" icon={PackageMinus} value={String(low.length)}
          note={low.length ? 'Time to reorder' : 'Nothing running low'} tone={low.length ? 'warn' : undefined} />
        <Tile label="Out of stock" href="/inventory/stock?show=out" icon={PackageX} value={String(out.length)}
          note={out.length ? 'Can’t be sold until restocked' : 'Everything is in stock'} tone={out.length ? 'warn' : undefined} />
      </div>

      <div className="sum-columns">
        <div className="sum-main">
          <Card title="Running low" action={<Link to="/inventory/stock?show=low">All running low</Link>}>
            {low.length ? (
              <ul className="sum-list">
                {low.slice(0, 6).map((p) => (
                  <li key={p.product_id}>
                    <span className="sum-list-main">
                      <Link to={`/inventory/stock/${p.product_id}`}>{p.name || p.sku}</Link>
                      <span className="sum-warn"> · {qty(p.qty_on_hand)} left</span>
                    </span>
                    {canBuy && <span className="sum-list-side"><Link to={`/purchase/po-form/new${p.stock_kind === 'material' ? '?for=materials' : ''}`}>Order more</Link></span>}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">Nothing is running low.</p>
            )}
          </Card>

          <Card title="Where the value is" action={<Link to="/inventory/stock">All stock</Link>}>
            {top.length ? (
              <ul className="sum-list">
                {top.map((p) => (
                  <li key={p.product_id}>
                    <span className="sum-list-main">
                      <Link to={`/inventory/stock/${p.product_id}`}>{p.name || p.sku}</Link>
                      <span className="sum-muted"> · {qty(p.qty_on_hand)} on hand</span>
                    </span>
                    <span className="sum-list-side">{money(stockValue(p))}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">Nothing is in stock yet. Stock arrives when you receive a purchase order.</p>
            )}
          </Card>
        </div>

        <div className="sum-side">
          {open && <Stages items={items} open={open} />}

          <Card title="Out of stock" action={<Link to="/inventory/stock?show=out">See all</Link>}>
            {out.length ? (
              <ul className="sum-list">
                {out.slice(0, 5).map((p) => (
                  <li key={p.product_id}>
                    <span className="sum-list-main"><Link to={`/inventory/stock/${p.product_id}`}>{p.name || p.sku}</Link></span>
                    <span className="sum-list-side sum-muted">{p.sku}</span>
                  </li>
                ))}
                {out.length > 5 && <li className="sum-more"><Link to="/inventory/stock?show=out">{out.length - 5} more</Link></li>}
              </ul>
            ) : (
              <p className="sum-muted">Everything is in stock.</p>
            )}
          </Card>

          <Card title="Recent moves" action={<Link to="/inventory/movements">All moves</Link>}>
            {moves.length ? (
              <ul className="sum-list">
                {moves.map((m, i) => (
                  <li key={m.movement_id ?? i}>
                    <span className="sum-list-main">
                      {m.name || m.sku}
                      <span className="sum-muted"> · {MOVE_TYPES[m.move_type] || m.move_type}{m.from_location || m.to_location ? ` · ${[m.from_location, m.to_location].filter(Boolean).join(' → ')}` : ''}</span>
                    </span>
                    <span className="sum-list-side"><span className="sum-muted">{fmtDay(m.movement_date, { year: false })} · </span>{qty(m.quantity)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sum-muted">No stock has been moved between locations yet.</p>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}

const STAGES_HELP = 'What your stock is worth at each stage of making things, as the balance sheet shows it: goods bought to resell, raw materials kept to make your products, the cost of runs still in progress, and finished goods made here.'

/** With Production: stock by stage, each a line of the balance sheet. */
function Stages({ items, open }) {
  const worth = (kind) => items.filter((p) => (p.stock_kind || 'goods') === kind).reduce((s, p) => s + stockValue(p), 0)
  const wip = (open || []).reduce((s, r) => s + Number(r.total_cost || 0), 0)
  const rows = [
    { label: 'Goods to resell', value: worth('goods'), to: '/inventory/stock' },
    { label: 'Raw materials', value: worth('material'), to: '/inventory/materials' },
    { label: 'Work in progress', value: wip, to: '/inventory/production?show=Open', note: open.length ? `${open.length} ${open.length === 1 ? 'run' : 'runs'} open` : 'No runs open' },
    { label: 'Finished goods', value: worth('made'), to: '/inventory/stock' },
  ]
  return (
    <Card title={<>By stage <HelpTip text={STAGES_HELP} /></>}>
      <ul className="sum-list">
        {rows.map((r) => (
          <li key={r.label}>
            <span className="sum-list-main"><Link to={r.to}>{r.label}</Link>{r.note && <span className="sum-muted"> · {r.note}</span>}</span>
            <span className="sum-list-side">{money(r.value)}</span>
          </li>
        ))}
      </ul>
    </Card>
  )
}
